import React, { useEffect, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, Button, Input, Box, VStack, HStack, Text, useToast,
} from "@chakra-ui/react";
import { searchColleagues } from "../../services/messagingService";
import SchemaBadge from "../messaging/SchemaBadge";

// Reusable "assign to user" picker: colleagues in the given clinic, matched on name, most recently active first.
export default function AssigneePickerModal({ isOpen, onClose, onSelect, title = "Assign to user", schema }) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery(""); setResults([]); setSelected(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!query || query.trim().length < 1) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const data = await searchColleagues(query, schema);
        setResults(data.users || []);
      } catch (err) {
        setResults([]);
        toast({ title: "Could not search users", description: err.message, status: "warning", duration: 3000, isClosable: true });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, schema, toast]);

  const confirm = () => {
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Input
              placeholder="Search by name..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (selected && e.target.value !== (selected.name || selected.email)) setSelected(null);
              }}
              autoFocus
            />
            {query && (
              <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
                {searching ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>Searching...</Text>
                ) : results.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No users found</Text>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {results.map((user) => (
                      <Box
                        key={user.id}
                        p={3}
                        cursor="pointer"
                        borderBottom="1px"
                        borderColor="gray.100"
                        bg={selected?.id === user.id ? "purple.50" : "white"}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => { setSelected(user); setQuery(user.name || user.email); setResults([]); }}
                      >
                        <HStack spacing={2}>
                          <Text fontWeight="bold" minW={0} overflowWrap="anywhere">{user.name || user.email}</Text>
                          <SchemaBadge schema={user.current_schema} />
                        </HStack>
                        {user.name && <Text fontSize="sm" color="gray.600">{user.email}</Text>}
                        {user.role && <Text fontSize="xs" color="gray.500">Role: {user.role}</Text>}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            )}
            {selected && (
              <Box p={3} border="1px" borderColor="purple.400" borderRadius="md" bg="purple.50">
                <Text fontSize="sm" fontWeight="bold" mb={1}>Selected:</Text>
                <HStack spacing={2}>
                  <Text fontWeight="bold" minW={0} overflowWrap="anywhere">{selected.name || selected.email}</Text>
                  <SchemaBadge schema={selected.current_schema} />
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="purple" onClick={confirm} isDisabled={!selected}>Assign</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
